import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  getUnreadPlanNotificationCount,
  listPlanNotifications,
  markPlanNotificationsRead,
} from '@/api/plans';
import type { PlanNotification } from '@/types/plan';
import { supabase } from '@/integrations/supabase/client';

interface UsePlanNotificationsResult {
  notifications: PlanNotification[];
  unreadCount: number;
  loading: boolean;
  refresh: () => Promise<void>;
  markRead: (notificationIds?: string[]) => Promise<void>;
}

export function usePlanNotifications(planId?: string): UsePlanNotificationsResult {
  const [notifications, setNotifications] = useState<PlanNotification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      setLoading(true);
      const { data: authData } = await supabase.auth.getSession();
      if (!authData.session) {
        setNotifications([]);
        setUnreadCount(0);
        return;
      }

      const [items, unread] = await Promise.all([
        listPlanNotifications(planId),
        getUnreadPlanNotificationCount(planId),
      ]);
      setNotifications(items);
      setUnreadCount(unread);
    } catch (error) {
      console.error('[usePlanNotifications] Failed to refresh notifications:', error);
      setNotifications([]);
      setUnreadCount(0);
    } finally {
      setLoading(false);
    }
  }, [planId]);

  useEffect(() => {
    void refresh();

    const intervalId = window.setInterval(() => {
      void refresh();
    }, 30000);

    return () => window.clearInterval(intervalId);
  }, [refresh]);

  const markRead = useCallback(async (notificationIds?: string[]) => {
    const targetIds = notificationIds && notificationIds.length > 0
      ? notificationIds
      : notifications.filter((notification) => !notification.is_read).map((notification) => notification.id);

    if (targetIds.length === 0) return;

    await markPlanNotificationsRead(targetIds);
    await refresh();
  }, [notifications, refresh]);

  return useMemo(() => ({
    notifications,
    unreadCount,
    loading,
    refresh,
    markRead,
  }), [notifications, unreadCount, loading, refresh, markRead]);
}
